/* eslint-disable eqeqeq */
import { useEffect, useRef } from 'react';
import { airStreamOptions } from '../../airStreamOptions';
import { useAirStreamOfferedIds } from '../../useAirStreamOfferedIds';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import { handleTabToNext, looseEq, truncDec } from '../../formKit';
import { formatDecimalDisplay } from '../../formUtils';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm, type AccessoryFormApi } from '../useAccessoryForm';
import {
  ABareInput,
  AccessorySelectHeading,
  AccSubtab,
  accList,
  APSelect,
  AutoSizeRow,
} from '../accessoryFieldKit';
import AccessoryMountingPopup, { type SpecialParametersProps } from '../AccessoryMountingPopup';
import { makeAutoSize } from '../useAutoSize';
import {
  ComplexMountingPlaneRow,
  ComplexPlaneRows,
  resolveComplexPlane,
} from '../complexMounting';
import type { AccessoryFormProps } from '../AccessoryFormHost';
import type { FormModel } from '../../autoSaveModel';

/**
 * Phase 6e2b-2 — LOUVER form (accessories/louver/{view/form.js 130,
 * tmpl/form.tmpl 474}, read in full). §51.2 row:
 *  - master_louver_id ← louver_params (7 live paramsFields incl. aw pair,
 *    noFetchCount, accessory reset extras);
 *  - makeAutoSize over louver_params.aw_width/aw_height (ratio 2, CMS
 *    snap-up — parity 331); faceVel default 1200;
 *  - same dataGetter SHAPE as damper minus the hood/intake web AND minus the
 *    IW machinery: louver's form.js does NOT import cms.cabinet.iw_planes —
 *    its mounting select iterates cms_accessory.mounting_plane raw;
 *  - blade_direction options narrowed by plane (model.blade_directions[@key]);
 *  - SelectionMode handler (callOnDispatcher) → Select heading; the tmpl
 *    sublists are statically non-clickable (selection-dialog-driven type).
 */
const config = ACCESSORY_FORM_CONFIGS.louver;

const parseParams = (model: FormModel | null, key: string): Record<string, unknown> => {
  try {
    return JSON.parse(String(model?.get(key) ?? '{}')) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const louverAutoSize = makeAutoSize({
  widthField: 'aw_width',
  heightField: 'aw_height',
  widthRatio: 2,
  getExisting: (model) => {
    const p = parseParams(model, 'louver_params');
    return { existingWidth: p.aw_width, existingHeight: p.aw_height };
  },
  applyResult: (data, width, height, modelJSON) => {
    const p = modelJSON.louver_params
      ? (JSON.parse(String(modelJSON.louver_params)) as Record<string, unknown>)
      : {};
    data.louver_params = JSON.stringify({ ...p, aw_width: width, aw_height: height });
  },
});

/** mounting_popup_special_parameters.tmpl (louver) — an empty col div. */
const LouverSpecialParameters = (_props: SpecialParametersProps) => (
  <div className="col-xs-12" />
);

/** blade-direction select narrowed by the current plane (damper/louver tmpls). */
export const BladeDirectionRow = ({ api }: { api: AccessoryFormApi }) => {
  const model = api.model;
  const planeId = api.position ? String(Number(api.position.get('mounting_plane_id')) || '') : '';
  const byPlane =
    (model?.get('blade_directions') as Record<
      string,
      Array<{ id: number; name?: string; disabled?: boolean }>
    > | null) ?? null;
  const options = (planeId && byPlane?.[planeId]) || [];
  const current = options.find((o) => looseEq(o.id, model?.get('blade_direction_id')));
  return (
    <li>
      <div className="single-line">
        <span className="selection">{current ? <span>{String(current.name ?? '')}</span> : null}</span>
        <select
          name="blade_direction_id"
          className="model_field"
          required
          value={
            model?.get('blade_direction_id') == null
              ? 'None'
              : String(model.get('blade_direction_id'))
          }
          onChange={(e) => api.commitModelField('blade_direction_id', e.target.value)}
        >
          <option value="None" className="hide">
            - -
          </option>
          {/* A louver built before the orientation rule can hold a direction its
              catalogue no longer offers; the server sends it back on its own
              plane marked `disabled`, so the row shows what the unit is built
              with without letting it be chosen again. */}
          {options.map((o) => (
            <option key={String(o.id)} value={String(o.id)} disabled={!!o.disabled}>
              {String(o.name ?? '')}
            </option>
          ))}
        </select>
        {/* bare `class="label "` in all three consumers' tmpls — louver:403,
            damper:549. No asterisk. */}
        <span className="label">Blade Direction</span>
      </div>
    </li>
  );
};

/** air-direction select (cms_accessory.air_direction — damper/louver/hood). */
export const AirDirectionRow = ({ api }: { api: AccessoryFormApi }) => {
  const model = api.model;
  const options = accList(api.cmsAccessory, 'air_direction');
  const current = options.find((o) => looseEq(o.id, model?.get('air_direction_id')));
  return (
    <li>
      <div className="single-line">
        <span className="selection">{current ? <span>{String(current.name ?? '')}</span> : null}</span>
        <select
          name="air_direction_id"
          className="model_field"
          required
          value={
            model?.get('air_direction_id') == null ? 'None' : String(model.get('air_direction_id'))
          }
          onChange={(e) => api.commitModelField('air_direction_id', e.target.value)}
        >
          <option value="None" className="hide">
            - -
          </option>
          {options.map((o) => (
            <option key={String(o.id)} value={String(o.id)}>
              {String(o.name ?? '')}
            </option>
          ))}
        </select>
        {/* louver:418, damper:561, hood:438 — all bare `class="label "`. */}
        <span className="label">Air Direction</span>
      </div>
    </li>
  );
};

/** casing-mount model_field radio group — inline mounting-subtab row present
 * in every casing-mount accessory tmpl (damper/louver/enclosure/disconnect/
 * adp_gage/jbox/vfd). `required` only on enclosure (tmpl label class). */
export const CasingMountRow = ({
  api,
  required,
}: {
  api: AccessoryFormApi;
  required?: boolean;
}) => {
  const current = api.model?.get('casing_mount_id');
  return (
    <li>
      <div>
        <div className="checkboxes-group">
          {accList(api.cmsAccessory, 'casing_mount').map((row) => (
            <label className="radio" key={String(row.id)}>
              <input
                type="radio"
                name="casing_mount_id"
                value={String(row.id)}
                checked={current == row.id}
                className="model_field"
                tabIndex={-1}
                onChange={(e) => api.commitModelField('casing_mount_id', e.target.value)}
              />{' '}
              {/* §JSX-WS — load-bearing. Every casing-mount tmpl newlines
                  between the `<input type="radio">` and `{{name}}`
                  (adp_gage form.tmpl:263-264, matched by louver/jbox/vfd/
                  enclosure/disconnect), and the browser collapses it to one
                  rendered space: `.radio` is inline-block, the input is
                  in-flow inline (`li input[type=radio] { margin: 0 5px 0 0;
                  position: relative }`), so the space sits mid-line and is
                  NOT stripped. JSX drops whitespace runs containing a
                  newline, so the value sat one space left of legacy. No space
                  between the two `.radio` labels — the second is floated
                  (`.checkboxes-group .radio + .radio { float: right }`). */}
              {String(row.name ?? '')}
            </label>
          ))}
        </div>
        <span className={`label${required ? ' required' : ''}`}> Casing Mount</span>
      </div>
    </li>
  );
};

/**
 * params select with selection mirror over a cms_accessory list.
 *
 * NO `required`. That flag is what draws the asterisk and nothing else —
 * `formKit.tsx:297` is `<span className={`label${required ? ' required' : ''}`}>`
 * — and louver's form.tmpl marks none of these: Manufacturer :29, Series :41 and
 * Frame Material :93 are all a bare `class="label "`, as is Width x Height :80.
 * `class="label required"` IS a real legacy idiom, but it belongs to the simpler
 * accessories (access_door, adp_gage, disconnect, dog_house, drain_pan,
 * enclosure, jbox, light, starter, uvc). The four selection-dialog types —
 * louver, damper, hood, opening — carry it on nothing at all:
 *
 *   git grep -c 'class="label required"' origin/dev \
 *     -- 'django/ahu/static/ahu/mvc/accessories/louver/tmpl/form.tmpl'   -> 0
 *
 * The `required` ATTRIBUTE on the `<select>` is a separate thing, is what legacy
 * does set here, and is untouched by this.
 */
const LParamsSelect = ({
  api,
  name,
  label,
  cmsPath,
  labelKey = 'name',
}: {
  api: AccessoryFormApi;
  name: string;
  label: string;
  cmsPath: string;
  labelKey?: string;
}) => (
  <APSelect
    api={api}
    target="master_louver_id"
    name={name}
    label={label}
    cmsPath={cmsPath}
    labelKey={labelKey}
    hideNone={false}
  />
);

/**
 * COMBO LOUVER (KA-5245) — the board row. `form.tmpl:44-60` replaces the plain
 * `aw_width`/`aw_height` pair with a THREE-input "Combo Enclosure W x H x D"
 * group under `{{#if model.is_combo}}` (measured live: `18.00 × 64.71 × 12.00`).
 *
 * A1.6 / §4 W4 — LIVE. The three inputs are `model_field`s, so legacy's
 * `accessory_view.js` auto-PATCHes `ahu/louvers/<id>` with just the changed key on
 * every edit; `api.commitModelField` is React's identical pipeline (the §49.2
 * auto-save model → `PATCH ahu/louvers/<id> {changed only}`). Committed on BLUR:
 * legacy binds `change` on `.model_field` (`accessory_view.js` has no keyup
 * binding for these), so one edit = one commit, and per-keystroke commits would
 * both spam PATCHes and fight the 2 dp reformat.
 *
 * A2.1 — DONE (wave 4): the canvas redraw. `mounting.js:59-66` listens on
 * `change:enclosure_width|_height|_depth|combo_faces|is_combo` →
 * `c_changed:affecting_view`; React's `MountingModel` has no accessory
 * subscription, so the substitute is those keys joining the `accessoryDrawSync`
 * canvasData snapshot (`accessoryDrawSync.ts`, mirroring
 * accessory_view.js:2058-2064) — an enclosure edit now redraws as well as
 * persists.
 *
 * Values are formatted with `formatDecimalDisplay` because legacy's
 * `data-formatter_type="decimal"` binding renders 2 dp + thousands separators; the
 * `key` re-sync keeps the uncontrolled box in step with the model (a server clamp
 * echo must be visible), while leaving typing untouched between renders.
 */
const ComboEnclosureRow = ({ api }: { api: AccessoryFormApi }) => {
  const model = api.model;
  const cell = (name: 'enclosure_width' | 'enclosure_height' | 'enclosure_depth') => {
    const shown = formatDecimalDisplay(model?.get(name));
    return (
      <input
        key={`${name}_${shown}`}
        className="model_field small-input"
        type="text"
        data-formatter_type="decimal"
        autoComplete="off"
        name={name}
        defaultValue={shown}
        onBlur={(e) => {
          // legacy `validatedNumberFields` (accessory_view.js:1209-1223): strip the
          // comma, clamp to data-min/data-max (both absent on these three), and —
          // `decimal` committed while not focused — write `toFixed(2)` back into the
          // box, which is also the string the PATCH then carries.
          const n = e.target.value.length ? Number(e.target.value.replace(/,/g, '')) : NaN;
          const next = Number.isNaN(n) ? '' : n.toFixed(2);
          e.target.value = next;
          if (next === shown.replace(/,/g, '')) return; // native `change` semantics
          api.commitModelField(name, next);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
    );
  };
  return (
    <li>
      {/* The `{' '}`s are load-bearing. `.unit`, `.small-input` and
          `.multiply-sign` are all inline/inline-block (ahu/styles.css:3481,
          4042-4048, 4036), so the NEWLINES form.tmpl:46-59 leaves between them
          render as five real ~4px spaces; JSX drops whitespace containing a
          newline, and the row lost all five. The `.label.big-label` floats left
          and stays put, so the whole triple crept leftwards instead — measured
          legacy vs port on the same sheet, the three inputs sat 4 / 12 / 20px
          left of legacy. No space before `.unit` (leading white space is
          stripped at the start of the line box) or before the float. */}
      <div className="multiply">
        <span className="unit" style={{ marginLeft: 15 }}>in</span>{' '}
        {cell('enclosure_width')}{' '}
        <span className="multiply-sign">x</span>{' '}
        {cell('enclosure_height')}{' '}
        <span className="multiply-sign">x</span>{' '}
        {cell('enclosure_depth')}
        <span className="label big-label">Combo Enclosure W x H x D</span>
      </div>
    </li>
  );
};

/** AW pair selects over cms aw_width/aw_height (louver variant — no OD). */
const AwPairRow = ({ api }: { api: AccessoryFormApi }) => {
  const engine = api.catalogueFields.master_louver_id;
  const widths = accList(api.cmsAccessory, 'aw_width');
  const heights = accList(api.cmsAccessory, 'aw_height');
  const awW = engine?.paramValue('aw_width');
  const awH = engine?.paramValue('aw_height');
  const pick = (name: 'aw_width' | 'aw_height', list: typeof widths, value: unknown) => (
    <>
      <span className="selection">
        {list.find((o) => looseEq(o[name], value)) ? <span>{truncDec(value)}</span> : null}
      </span>
      <select
        name={name}
        className="louver_params_field medium-input"
        required
        value={value == null || value === '' ? 'None' : String(value)}
        onChange={(e) => api.commitParamsField('master_louver_id', name, e.target.value)}
      >
        <option value="None">- -</option>
        {list.map((o) => (
          <option key={String(o[name])} value={String(o[name])}>
            {truncDec(o[name])}
          </option>
        ))}
      </select>
    </>
  );
  return (
    <li>
      {/* legacy louver tmpl :44-64 has NO unit span on this row (6e2b
          verifier: spurious `in` unit removed for parity). */}
      <div className="single-line">
        {pick('aw_width', widths, awW)}
        {pick('aw_height', heights, awH)}
        {/* louver form.tmpl:80 — `<span class="">` on both, no asterisk. */}
        <span className="label">
          <span>Width</span> x <span>Height</span>
        </span>
      </div>
    </li>
  );
};

/**
 * ★★ COMBO LOUVER (KA-5245) — QUIRK Q1: THE BOARD ROW's `louver_params` WRITE.
 *
 * Measured on legacy immediately after a combo is inserted (inventory §4 W1):
 *
 *   PATCH /api/v1.0/ahu/louvers/25774
 *   {"louver_params":"{\"aw_width\":68,\"aw_height\":34}"}          → 200
 *
 * It comes from the BOARD ROW, not the selector: `accessory_view.js:769-780` runs
 * `autoSize(false, model, data)` on every render after the first whenever
 * `airflow_actual` is set, the louver's `makeAutoSize` finds no `aw_width`/
 * `aw_height` in `louver_params`, computes them (airflow 20 000 at the 1200 fpm
 * default → `floor(sqrt(144·20000/(1200·2))) = 34`, `2·34 = 68`, each snapped up to
 * the first CMS row) and `model.set(data)` hands them to the §49.2 auto-save.
 *
 * Those two keys are MEANINGLESS for a combo — `master_louver_id` is null and the
 * sizes are per-face — and they SURVIVE alongside the real identity keys: the
 * measured persist body carries `{"aw_width":68,"aw_height":34,"mfg_id":47,…}`.
 * It is a legacy defect, but it is a legacy defect that LANDS IN THE DATABASE, so
 * a React stack that skips it leaves the two stacks' `louver_params` permanently
 * different for the same user actions. Parity-only: mirror it.
 *
 * React already computes the same stamp (`useAccessoryForm`'s render-time
 * `runAutoSize(false)` → `m.set(data)`), but its §49.2 model is userArmed-gated —
 * saves are structurally impossible until a real user gesture — so the value stays
 * in memory and never reaches the wire. This effect is that missing write and
 * nothing else: it fires at most ONCE, only for a combo, only while the model is
 * still un-armed (i.e. the change can only be the programmatic stamp), and only
 * when `louver_params` actually moved away from the row the server sent.
 *
 * ⚠ SCOPE, deliberate: legacy does this for plain louvers and dampers too. That
 * divergence is PRE-EXISTING (it is the phase-6e2b userArmed gate, a documented
 * data-safety deviation covering every accessory type) and lifting the gate
 * globally would make merely opening the board write to every under-specified row
 * — out of this port's blast radius. Reported, not fixed here.
 */
const useComboLouverParamsEcho = (api: AccessoryFormApi, rowId: number): void => {
  const dispatch = useAppDispatch();
  const sent = useRef(false);
  const loaded = useRef<string | null>(null);
  useEffect(() => {
    const m = api.model;
    if (!m) return;
    const current = String(m.get('louver_params') ?? '');
    if (loaded.current === null) {
      loaded.current = current; // the row as the server sent it
      return;
    }
    if (sent.current || !m.get('is_combo') || m.userArmed) return;
    if (current === loaded.current) return; // the autoSize stamp has not landed yet
    sent.current = true;
    void dispatch(
      ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
        plural: 'louvers',
        id: rowId,
        data: { louver_params: current },
      })
    );
  });
};

const LouverForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting', 'performance'],
    autoSize: louverAutoSize,
    autoSizeGetState: (obj) => {
      try {
        const p = JSON.parse(String(obj?.louver_params ?? '{}')) as Record<string, unknown>;
        return { aw_width: p.aw_width, aw_height: p.aw_height };
      } catch {
        return {};
      }
    },
    getFaceVelocityArea: (data) => {
      try {
        const m = data.model as Record<string, unknown> | undefined;
        const p = JSON.parse(String(m?.louver_params ?? '{}')) as Record<string, unknown>;
        const w = Number(p.aw_width);
        const h = Number(p.aw_height);
        return w && h ? w * h : null;
      } catch {
        return null;
      }
    },
    defaultFaceVelocity: 1200,
  });
  // ★ Above the preloader early-return below: a hook that runs only
  // after the form has loaded changes the hook count between renders
  // (React #310, "Rendered more hooks than during the previous render").
  const offeredAirStreams = useAirStreamOfferedIds();
  // ★ Q1 — the combo board row's `louver_params` auto-save (see above).
  useComboLouverParamsEcho(api, row.id);
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  // louver mounting select = raw cms planes (no IW machinery — form.js).
  const cmsPlanes = (api.cmsAccessory?.mounting_plane as Array<Record<string, unknown>>) ?? [];
  const resolved = resolveComplexPlane(
    api,
    undefined,
    cmsPlanes as Array<Record<string, unknown> & { id: number }>
  );
  const airStreams = airStreamOptions(
    accList(api.cmsAccessory, 'air_stream'),
    offeredAirStreams,
    model.get('air_stream_id')
  );

  return (
    <>
      <AccessorySelectHeading api={api} type="louver" />
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist">
          <AccSubtab api={api} type="louver" cid={cid} name="properties" sublistClass="max-height-auto non-clickable">
            <li>
              <div>
                <ABareInput api={api} name="tag" formatter="none" extraClass="text-left" />
                <i className="text-clear icon-close-1" />
                <span className="label">Tag</span>
              </div>
            </li>
            <LParamsSelect api={api} name="mfg_id" label="Manufacturer" cmsPath="manufacturer" />
            <LParamsSelect api={api} name="series_id" label="Series" cmsPath="series" labelKey="series" />
            {/* form.tmpl:44-60 `{{#if model.is_combo}}` … `{{else}}` — a combo
                shows the enclosure triple INSTEAD of the airway pair. */}
            {model.get('is_combo') ? <ComboEnclosureRow api={api} /> : <AwPairRow api={api} />}
            <LParamsSelect api={api} name="frame_mtl_id" label="Frame Material" cmsPath="frame_mtl" />
          </AccSubtab>

          <AccSubtab
            api={api}
            type="louver"
            cid={cid}
            name="mounting"
            label="Mounting"
            /* form.tmpl:98 — `<li class="mounting-section-wrapper">`. The class
               is the only selector for styles.css:5595-5604, which narrows the
               two `position_field medium-input` boxes from the generic 24.58%
               (styles.css:4348) to 10.83% and spaces them by 10px. Without it
               the distance rows overflow the row width and the float:left
               `.label` (styles.css:3474) drops to a line of its own. */
            liClass="mounting-section-wrapper"
            sublistClass={` non-clickable${model.get('master_louver_id') === null ? ' non-clickable' : ''}`}
          >
            <ComplexMountingPlaneRow
              api={api}
              cabinetPlanes={cmsPlanes as Array<Record<string, unknown> & { id: number }>}
              selectionText={resolved.planeName}
              selectedValue={
                api.position && Number(api.position.get('mounting_plane_id'))
                  ? String(api.position.get('mounting_plane_id'))
                  : 'None'
              }
            />
            <ComplexPlaneRows
              api={api}
              variant="louver"
              planeValue={resolved.planeValue}
              iwPlane={null}
            />
            <CasingMountRow api={api} />
            {/* form.tmpl:381-407 wraps Blade Direction in
                `{{#unless model.is_combo}}` — derived for a combo, and
                `blade_direction_id` stays null on the parent. Air Direction
                (:408-420) is OUTSIDE that block → still editable. */}
            {model.get('is_combo') ? null : <BladeDirectionRow api={api} />}
            <AirDirectionRow api={api} />
          </AccSubtab>

          <AccSubtab
            api={api}
            type="louver"
            cid={cid}
            name="performance"
            label="Performance"
            sublistClass="performance-sublist non-clickable"
          >
            <li>
              <div>
                <span className="unit">CFM</span>
                <span className="selection">
                  {airStreams.find((o) => looseEq(o.id, model.get('air_stream_id'))) ? (
                    <span>
                      {String(
                        airStreams.find((o) => looseEq(o.id, model.get('air_stream_id')))?.name ?? ''
                      )}
                    </span>
                  ) : null}
                </span>
                <select
                  className="coli-medium model_field"
                  name="air_stream_id"
                  required
                  value={
                    model.get('air_stream_id') == null ? 'None' : String(model.get('air_stream_id'))
                  }
                  onChange={(e) => api.commitModelField('air_stream_id', e.target.value)}
                >
                  <option value="None">- -</option>
                  {airStreams.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.name ?? '')}&nbsp;
                    </option>
                  ))}
                </select>
                <ABareInput
                  api={api}
                  name="airflow_actual"
                  formatter="integer"
                  extraClass="coli-medium"
                  min={Number(model.get('min_airflow')) || 0}
                  max={Number(model.get('max_airflow')) || 400000}
                />
                <span className="label">Air Stream &amp; Flow</span>
              </div>
            </li>
            <AutoSizeRow
              api={api}
              tooltip="Louver will be automatically be sized based on 1:1 width x height ratio, airflow and desired velocity"
            />
            <li>
              <div>
                <span className="unit short-unit">fpm</span>
                <input
                  type="text"
                  data-formatter_type="decimal"
                  autoComplete="off"
                  name="face_velocity_actual"
                  className="coli-medium"
                  readOnly
                  tabIndex={-1}
                  value={String(model.get('face_velocity_actual') ?? '')}
                  onChange={() => undefined}
                />
                <span className="unit long-unit">in WC</span>
                <input
                  type="text"
                  autoComplete="off"
                  name="apd"
                  className="coli-medium"
                  readOnly
                  tabIndex={-1}
                  value={String(model.get('apd') ?? '')}
                  onChange={() => undefined}
                />
                <span className="label">Face Velocity | APD</span>
              </div>
            </li>
          </AccSubtab>
        </ul>
      </form>
      <div id={`popup_accessory_mounting_${row.id}`} className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
      <AccessoryMountingPopup
        api={api}
        typeName="louver"
        typeValue="louver"
        SpecialParameters={LouverSpecialParameters}
      />
    </>
  );
};

export default LouverForm;
